import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import moment from "moment";

export const useBookingReports = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [detailedBookings, setDetailedBookings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]); // New state for branches

  // Get user role from localStorage
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const userRole = userDetails.role;

  const [filters, setFilters] = useState({
    startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    doctorId: "",
    departmentId: "",
    bookingSource: "",
    status: "",
    branchId: "all", // Default to "all" for hospital_admin
  });

  // Fetch branches (only for hospital_admin)
  const fetchBranches = async () => {
    if (userRole !== "hospital_admin") return;

    try {
      const response = await axiosInstance.get("/hospitals/branches");
      const branchesData = response.data.branches || response.data || [];
      setBranches(branchesData);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  // Fetch doctors
  const fetchDoctors = async () => {
    try {
      const response = await axiosInstance.get("/doctors/details");
      setDoctors(response.data.doctors || response.data || []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get("/departments/");
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  // Fetch reports
  const fetchReports = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await axiosInstance.get(
        `/reports/bookings?${params.toString()}`
      );

      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      alert("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed bookings
  const fetchDetailedBookings = async (page = 1) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      params.append("page", page);
      params.append("limit", 50);

      const response = await axiosInstance.get(
        `/reports/bookings/detailed?${params.toString()}`
      );

      setDetailedBookings(response.data.bookings);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching detailed bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  // Apply filters
  const handleApplyFilters = () => {
    fetchReports();
    fetchDetailedBookings();
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      doctorId: "",
      departmentId: "",
      bookingSource: "",
      status: "",
      branchId: "all", // Reset to "all" for hospital_admin
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!detailedBookings.length) return;

    const headers = [
      "Booking ID",
      "Patient Name",
      "Phone",
      "Doctor",
      "Department",
      "Date",
      "Time",
      "Token",
      "Status",
      "Source",
    ];

    const rows = detailedBookings.map((b) => [
      b.bookingId,
      b.patient.name,
      b.patient.phoneNumber,
      b.doctor.name,
      b.department.name,
      moment(b.bookingDate).format("DD-MM-YYYY"),
      `${b.startTime} - ${b.endTime}`,
      b.tokenNumber || "N/A",
      b.status,
      b.bookingSource,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `booking-report-${moment().format("YYYY-MM-DD")}.csv`;
    a.click();
  };

  // Initialize data on mount
  useEffect(() => {
    fetchBranches(); // Fetch branches for hospital_admin
    fetchDoctors();
    fetchDepartments();
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    reportData,
    detailedBookings,
    pagination,
    doctors,
    departments,
    branches, // Add branches to return
    userRole, // Add userRole to return
    filters,
    handleFilterChange,
    handleApplyFilters,
    handleResetFilters,
    exportToCSV,
    fetchDetailedBookings,
  };
};
